import { useState, useEffect } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { motion, AnimatePresence } from "framer-motion";

const API_KEY = "AIzaSyDDv9FFGFzMbPDsaPgRF8rIh5RP12uB98I";
const CALENDAR_ID =
  "1325b8c6067825f0531ccfe2635bb3d0fdd717e82fae00312289e78f06e5e7b0@group.calendar.google.com";

export default function DayViewCalendar({ currentDay, setCurrentDate }) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, [currentDay]);

  const fetchEvents = async () => {
    const startOfDay = currentDay.startOf("day").toISOString();
    const endOfDay = currentDay.endOf("day").toISOString();

    const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
      CALENDAR_ID
    )}/events?key=${API_KEY}&timeMin=${startOfDay}&timeMax=${endOfDay}&singleEvents=true&orderBy=startTime`;

    try {
      const response = await axios.get(url);
      setEvents(response.data.items.filter((e) => e.start?.dateTime));
    } catch (error) {
      console.error("Erreur lors du chargement des événements", error);
    }
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const isToday = dayjs().isSame(currentDay, "day");
  const currentTimeTop = (dayjs().hour() + dayjs().minute() / 60) * 64;

  return (
    <div className="max-w-full mx-auto bg-white shadow-xl rounded-lg p-6">
      <div className="grid grid-cols-4 gap-4">
        {/* Grille principale */}
        <div className="col-span-3">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentDay.format()}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="h-[700px] overflow-auto border rounded-lg relative"
            >
              {/* Ligne rouge = heure actuelle */}
              {isToday && (
                <div
                  className="absolute left-[80px] right-0 h-0.5 bg-red-500 z-50"
                  style={{ top: `${currentTimeTop}px` }}
                />
              )}

              {hours.map((hour) => (
                <div
                  key={hour}
                  className="grid grid-cols-[80px_1fr] h-16 border-b relative"
                >
                  {/* Heure */}
                  <div className="text-sm text-gray-400 flex items-start justify-end pr-3 pt-2">
                    {dayjs().hour(hour).minute(0).format("h A")}
                  </div>

                  {/* Événements commencés à cette heure */}
                  <div className="relative">
                    {events.map((event, i) => {
                      const start = dayjs(event.start.dateTime);
                      const end = dayjs(event.end.dateTime);
                      const isSameHour = start.hour() === hour;

                      if (isSameHour) {
                        const top = `${(start.minute() / 60) * 64}px`;
                        const height = `${(end.diff(start, "minute") / 60) * 64 || 64}px`;

                        return (
                          <div
                            key={i}
                            className={`absolute left-0 right-4 p-2 rounded shadow-sm border-l-4 text-xs ${
                              i % 2 === 0
                                ? "bg-blue-50 border-blue-400 text-blue-800"
                                : "bg-pink-50 border-pink-400 text-pink-800"
                            }`}
                            style={{ top, height }}
                          >
                            <div className="font-semibold">{event.summary || "Sans titre"}</div>
                            <div className="text-[10px] text-gray-500">
                              {start.format("HH:mm")} - {end.format("HH:mm")}
                            </div>
                            {event.location && (
                              <div className="text-[10px] text-blue-700">
                                {event.location}
                              </div>
                            )}
                          </div>
                        );
                      }

                      return null;
                    })}
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mini Calendrier latéral */}
        <div className="col-span-1 bg-white rounded-lg p-4 border shadow-sm w-full">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCurrentDate(currentDay.subtract(1, "month"))}>
              <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
            </button>
            <div className="text-sm font-semibold text-gray-800">
              {currentDay.format("MMMM YYYY")}
            </div>
            <button onClick={() => setCurrentDate(currentDay.add(1, "month"))}>
              <ChevronRightIcon className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          <div className="grid grid-cols-7 text-center text-xs text-gray-500 font-medium mb-1">
            {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
              <div key={i} className="py-1">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1 text-xs mt-1">
            {Array.from({ length: 42 }, (_, i) => {
              const startOfMonth = currentDay.startOf("month");
              const startDate = startOfMonth.startOf("week");
              const day = startDate.add(i, "day");

              const isCurrentMonth = day.month() === currentDay.month();
              const isToday = dayjs().isSame(day, "day");
              const isSelected = currentDay.isSame(day, "day");

              return (
                <button
                  key={i}
                  onClick={() => setCurrentDate(day)}
                  className={`w-8 h-8 flex items-center justify-center rounded-full mx-auto transition
                    ${
                      isSelected
                        ? "bg-black text-white"
                        : isToday
                        ? "text-violet-600 font-semibold"
                        : isCurrentMonth
                        ? "text-gray-800 hover:bg-gray-100"
                        : "text-gray-300"
                    }`}
                >
                  {day.date()}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
